import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
    NewspaperIcon,
    ChatBubbleLeftRightIcon,
    UserIcon,
    GlobeAltIcon,
    StarIcon,
    UsersIcon,
    BriefcaseIcon,
    EnvelopeIcon,
} from "@heroicons/react/24/outline";

const MySwal = withReactContent(Swal);

export default function Dashboard() {
    const [statistics, setStatistics] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Map of icons for each statistic
    const iconMap = {
        Blogs: NewspaperIcon,
        Consultations: ChatBubbleLeftRightIcon,
        Contacts: UserIcon,
        Countries: GlobeAltIcon,
        Feedbacks: StarIcon,
        Partners: UsersIcon,
        Projects: BriefcaseIcon,
        Subscriptions: EnvelopeIcon,
    };

    // Fetch statistics
    useEffect(() => {
        const fetchStatistics = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get("https://ligasos-laravel.ligasos.co/api/stats");
                setStatistics(Array.isArray(response.data.data) ? response.data.data : []);
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Failed to fetch statistics. Please try again.",
                    confirmButtonColor: "#B39359",
                });
                setStatistics([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchStatistics();
    }, []);

    return (
        <section className="min-h-screen py-12 px-4">
            <div className="">
                <div className="mb-10">
                    <h2 className="text-4xl md:text-5xl font-extrabold text-[#0B2C3D] tracking-tight drop-shadow-lg">
                        Dashboard
                    </h2>
                    <p className="mt-2 text-lg text-[#0B2C3D]">
                        Key Statistics at a Glance
                    </p>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#B39359]"></div>
                    </div>
                ) : statistics.length === 0 ? (
                    <div className="text-center bg-white/10 backdrop-blur-lg p-8 rounded-2xl shadow-xl">
                        <p className="text-lg font-medium text-gray-200">No statistics available.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {statistics.map((stat, index) => {
                            const IconComponent = iconMap[stat.title_en] || UserIcon;
                            return (
                                <div
                                    key={index}
                                    className="relative bg-white/10 backdrop-blur-xl p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-[#B39359]/20 group"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-[#B39359]/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    <div className="relative flex items-center gap-4">
                                        <div className="p-4 bg-[#0B2C3D]/80 rounded-full shadow-md group-hover:scale-110 transition-transform duration-300">
                                            <IconComponent className="w-10 h-10 text-[#B39359] group-hover:text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-[#0B2C3D] group-hover:text-[#B39359] transition-colors duration-300">
                                                {stat.title_en}
                                            </h3>
                                            <p className="text-3xl font-extrabold text-[#B39359] mt-1">
                                                {stat.count}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
}